<template>
  <Select
    v-if="$can('update', subject('Allocation', allocation), `${mode}ResponsibleStation`)"
    :model-value="stationId"
    class="w-56 min-w-0 max-w-full"
    :options="stationOptions"
    :placeholder="placeholder ?? t('station')"
    :disabled="isUpdatingAllocation"
    variant="inline"
    @update:model-value="handleStationUpdate"
  />
  <span v-else>
    <slot>
      {{
        allocation[`${mode}ResponsibleStation`]
          ? getLocalizedField(allocation[`${mode}ResponsibleStation`]!.info)
          : '—'
      }}
    </slot>
  </span>
</template>

<script setup lang="ts">
import Select from '@/components/Select.vue';
import type { Allocation } from '@/entities/allocations/allocation.entity';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { useLocalizedFields } from '@/hooks/use-localized-fields';
import { useUpdateAllocation } from '@/queries/use-allocations';
import { useStations } from '@/queries/use-stations';
import { Alert } from '@/services/alert.service';
import { subject } from '@casl/ability';
import { useQueryClient } from '@tanstack/vue-query';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  allocation: Allocation;
  bookingId: string;
  mode: 'pickup' | 'dropoff';
  placeholder?: string;
  confirmChangeLocationLabel?: string;
  excludeStationId?: string | null;
  homeStationId?: string | null;
}>();

const { t } = useI18n();
const { getLocalizedField } = useLocalizedFields();
const queryClient = useQueryClient();
const { mutateAsync: updateAllocation, isPending: isUpdatingAllocation } = useUpdateAllocation();

const stationId = ref<string | null>(
  props.allocation[`${props.mode}ResponsibleStation`]?.id ?? null,
);
watch(
  () => props.allocation[`${props.mode}ResponsibleStation`]?.id,
  (id) => {
    stationId.value = id ?? null;
  },
);

const { data: stationsData } = useStations(500);
const stations = useFlattenPaginatedData(stationsData);

const stationOptions = computed(() =>
  stations.value
    ?.filter((station) => !props.excludeStationId || station.id !== props.excludeStationId)
    .map((station) => ({
      label:
        getLocalizedField(station.info) +
        (props.homeStationId &&
        station.id === props.homeStationId &&
        props.allocation[`${props.mode}ResponsibleStation`]?.id !== props.homeStationId
          ? ' 🏠'
          : ''),
      value: station.id,
    })),
);

const handleStationUpdate = async (newVal: string | null | undefined) => {
  if (newVal === props.allocation[`${props.mode}ResponsibleStation`]?.id) return;

  const { isConfirmed } = await Alert.fire({
    text: t('stationChangedWarningText', {
      location: props.confirmChangeLocationLabel ?? t('station'),
    }),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: t('save'),
    cancelButtonText: t('cancel'),
  });
  if (!isConfirmed) return;

  stationId.value = newVal ?? null;
  await updateAllocation({
    id: props.allocation.id,
    allocation: { [`${props.mode}ResponsibleStationId`]: stationId.value },
  });
  await queryClient.invalidateQueries({ queryKey: ['bookings', props.bookingId] });
};
</script>

<i18n lang="json">
{
  "en": {
    "station": "Station",
    "save": "Save",
    "cancel": "Cancel",
    "stationChangedWarningText": "Do you really want to change the {location}?"
  },
  "de": {
    "station": "Station",
    "save": "Speichern",
    "cancel": "Abbrechen",
    "stationChangedWarningText": "Bist du sicher, dass du den {location} ändern möchtest?"
  },
  "es": {
    "station": "Estación",
    "save": "Guardar",
    "cancel": "Cancelar",
    "stationChangedWarningText": "¿Seguro que desea cambiar {location}?"
  }
}
</i18n>
